import { Avatar, Box, Card, Icon, ResourceItem, ResourceList, Text, TextField, useBreakpoints } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SearchIcon } from '@shopify/polaris-icons';
import helpers from 'helpers/index';
import { TypedUser } from 'interface/user.model';
import { useSearchUser } from 'queries/user.query';
import { useAuth } from 'context/AuthContext';

type TypedSearchUser = {
  onClose?: (val: TypedUser) => void;
  title?: string;
  placeholder?: string;
  /** "user" | "customer" | "agency" | "collaborator" | "sale" | "sale_admin" | "admin" | "visitor" */
  user_role?: string;
  disabled?: boolean;
  readonly?: boolean;
  includeMe?: boolean;
  noWrap?: boolean;
};

/**
 * @param  onClose callback with bigint userid
 * @returns
 */
export default function QuickSearchUser({
  disabled,
  onClose,
  title = 'Tìm một người dùng',
  placeholder = 'Tìm một người dùng',
  readonly = false,
  user_role = '',
  includeMe = false,
  noWrap = false,
}: TypedSearchUser) {
  const { mutateAsync: searchUser, isSuccess } = useSearchUser();

  const initialQuery = {
    query: '',
    user_role: user_role,
    user_status: 1,
    sort: 'createdAt:desc',
    limit: 30,
  };

  const { user: currentUserData } = useAuth();

  const [mainQuery, setMainQuery] = useState(initialQuery);

  /**
   * User list
   */
  const [userList, setUserList] = useState<any[]>([]);

  const onQueryToServer = useCallback(async (queryString) => {
    try {
      let data = await searchUser(queryString);
      // @ts-ignore
      setUserList(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (mainQuery) {
      onQueryToServer(mainQuery);
    }
  }, [mainQuery]);

  /**
   * Assign cho ai đóa
   */
  const [userQuickSearchText, setUserQuickSearchText] = useState<string>('');
  const [userListForQuickOption, setUserListForQuickOption] = useState([]);

  const onQueryToServerDebound = useCallback((keyword) => {
    setMainQuery((old) => ({ ...old, ...{ query: keyword } }));
  }, []);

  useEffect(() => {
    let innerUser = includeMe
      ? [
          {
            user_id: currentUserData.user_id,
            display_name: `Tôi`,
            user_role: currentUserData.user_role,
            media: <Avatar source={currentUserData.user_avatar} name={currentUserData?.display_name} initials={currentUserData.display_name[0]} />,
          },
        ]
      : [];
    if (userList)
      for (let user of userList) {
        let display_name = user.display_name || user.user_email || user.user_phonenumber || user.user_login;
        let user_phonenumber = user.user_phonenumber || user.user_email || '';
        innerUser.push({
          user_id: user.user_id,
          display_name: `${display_name} ${user_phonenumber}`,
          user_role: user.user_role,
          media: <Avatar source={user.user_avatar} name={display_name} initials={display_name[0]} />,
        });
      }
    setUserListForQuickOption(innerUser);
  }, [userList, currentUserData]);

  const onChangeMainQueryCallback = useMemo(() => {
    return helpers.debounce((_value: string) => {
      onQueryToServerDebound?.call(this, _value);
    }, 400);
  }, []);

  const updateUserText = useCallback((value: string) => {
    setUserQuickSearchText(value);
    onChangeMainQueryCallback(value);
  }, []);

  const updateSelection = useCallback(
    (user_id: string) => {
      const matchedOption = userListForQuickOption.find((option) => {
        return option.user_id === user_id;
      });
      setUserQuickSearchText(matchedOption?.display_name || '');
      onClose(matchedOption);
    },
    [userListForQuickOption]
  );

  const { smUp } = useBreakpoints();

  return (
    <Box minWidth="250px" maxWidth={smUp ? `300px` : '100%'} padding={{ xs: '0', sm: '0', md: '200' }} paddingInline={noWrap ? '0' : '200'}>
      <Card>
        <TextField
          readOnly={readonly}
          onChange={updateUserText}
          label={title}
          disabled={disabled}
          value={userQuickSearchText}
          prefix={<Icon source={SearchIcon} tone="base" />}
          placeholder={placeholder}
          autoComplete="off"
          clearButton
          onClearButtonClick={() => updateUserText('')}
        />
      </Card>
      <ResourceList
        emptyState={
          isSuccess && helpers.isEmpty(userListForQuickOption) ? (
            <Box padding={'400'}>
              <Text as="p" tone="subdued">
                Chưa thấy một ai!
              </Text>
            </Box>
          ) : null
        }
        resourceName={{ singular: 'User', plural: 'user' }}
        items={userListForQuickOption}
        renderItem={(item) => {
          const { user_id, display_name, user_role, media } = item;

          return (
            <ResourceItem
              disabled={disabled || readonly}
              verticalAlignment="center"
              id={user_id}
              onClick={() => updateSelection(user_id)}
              media={media}
            >
              <Text variant="bodyMd" as="p">
                {display_name} (<i>{user_role}</i>)
              </Text>
            </ResourceItem>
          );
        }}
      />
    </Box>
  );
}

